import React, { useEffect, useState } from "react";
import { RotatingLines } from "react-loader-spinner";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/Auth";
import {
  ArrowLeft,
  User,
  Phone,
  MapPin,
  FileText,
  DollarSign,
  CreditCard,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Package,
  Receipt,
} from "lucide-react";
import SupplierTransaction from "../../Transactions/SupplierTransaction";

const SupplierDetail = () => {
  const navigate = useNavigate()
  const { supplierId } = useParams();
  const { requestWithAuth } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("invoices");

  // Financial summary
  const [financeSummary, setFinanceSummary] = useState(null);

  const fetchSupplierDetails = async () => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/supplier/${supplierId}/details/`,
        "GET"
      );
      setSupplier(data);
    } catch (error) {
      console.error("Error fetching supplier details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplierInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const data = await requestWithAuth(
        `/supplier/${supplierId}/invoices/`,
        "GET"
      );
      setInvoices(data?.results || []);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching supplier invoices:", error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchSupplierTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const data = await requestWithAuth(
        `/supplier/transaction/?supplier=${supplierId}`,
        "GET"
      );
      setTransactions(data?.results || []);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching supplier transactions:", error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const data = await requestWithAuth(
        `/supplier/${supplierId}/financial-summary/`,
        "GET"
      );
      setFinanceSummary(data);
    } catch (error) {
      console.error("Error fetching financial summary:", error);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && setCurrentPage(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchSupplierDetails();
    fetchFinancialSummary();
  }, []);

  useEffect(() => {
    if (activeTab === "invoices") {
      fetchSupplierInvoices();
    } else {
      fetchSupplierTransactions();
    }
  }, [currentPage, activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex items-center gap-3 text-gray-600">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span>Loading supplier details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Suppliers</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Details</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                {supplier?.name || "Loading..."}
              </h1>
            </div>
            <div className="flex gap-3">
              {financeSummary?.outstanding_balance > 0 && (
                <button
                  onClick={() => setPaymentModalOpen(true)}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
                >
                  <CreditCard size={20} />
                  Make Payment
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Supplier Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User size={20} />
              Supplier Information
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <User size={14} />
                  Supplier Name
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {supplier?.name || "N/A"}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <User size={14} />
                  Contact Person
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {supplier?.contact_person || "N/A"}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Phone size={14} />
                  Phone Number
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {supplier?.contact_person_number || "N/A"}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <MapPin size={14} />
                  Address
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {supplier?.address || "N/A"}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <DollarSign size={14} />
                  Opening Balance
                </div>
                <p className="text-sm font-medium text-gray-900">
                  GH₵ {parseFloat(supplier?.opening_balance ?? 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Total Supply */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="text-blue-600" size={20} />
                </div>
                <h3 className="text-sm font-semibold text-blue-900">
                  Total Supply Received
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-3xl font-bold text-blue-600 mb-1">
                GH₵{" "}
                {financeSummary?.total_supplies?.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || "0.00"}
              </p>
              <p className="text-xs text-gray-500">Total invoice amount</p>
            </div>
          </div>

          {/* Amount Paid */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 bg-green-50 border-b border-green-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="text-green-600" size={20} />
                </div>
                <h3 className="text-sm font-semibold text-green-900">
                  Amount Paid
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-3xl font-bold text-green-600 mb-1">
                GH₵{" "}
                {financeSummary?.amount_paid?.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || "0.00"}
              </p>
              <p className="text-xs text-gray-500">Total payments made</p>
            </div>
          </div>

          {/* Outstanding Balance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 bg-red-50 border-b border-red-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="text-red-600" size={20} />
                </div>
                <h3 className="text-sm font-semibold text-red-900">
                  Outstanding Balance
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-3xl font-bold text-red-600 mb-1">
                GH₵{" "}
                {financeSummary?.outstanding_balance?.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || "0.00"}
              </p>
              <p className="text-xs text-gray-500">Remaining balance</p>
            </div>
          </div>
        </div>

        {/* Tabbed Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Tab Header */}
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => handleTabChange("invoices")}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === "invoices"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                <FileText size={18} />
                Invoice History
              </button>
              <button
                onClick={() => handleTabChange("transactions")}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === "transactions"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                <Receipt size={18} />
                Transaction History
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="overflow-x-auto">
            {activeTab === "invoices" ? (
              // Invoice History Content
              invoicesLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <span>Loading invoices...</span>
                  </div>
                </div>
              ) : invoices?.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Invoice Number
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Invoice Date
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Invoice Amount
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Posted By
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Narration
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoices.map((invoice, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6 text-sm font-medium text-gray-900">
                          {invoice?.invoice_number}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700">
                          {invoice?.invoice_date}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700">
                          GH₵{" "}
                          {parseFloat(invoice?.invoice_amount || 0).toFixed(2)}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700">
                          {invoice?.posted_by}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700">
                          {invoice?.quantity}
                        </td>
                        <td className="py-4 px-6">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${invoice?.invoice_narration === "Supply"
                              ? "bg-green-100 text-green-800"
                              : "bg-orange-100 text-orange-800"
                              }`}
                          >
                            {invoice?.invoice_narration}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <button
                            onClick={() => {
                              navigate(`/inventory/invoice/${invoice?.id}/details`);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            View Details
                          </button>

                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-20 text-center">
                  <div className="text-gray-400 mb-2">
                    <FileText className="mx-auto h-12 w-12" strokeWidth={1.5} />
                  </div>
                  <p className="text-gray-500 text-sm">
                    No invoices found for this supplier
                  </p>
                </div>
              )
            ) : // Transaction History Content
              transactionsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <span>Loading transactions...</span>
                  </div>
                </div>
              ) : transactions?.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Transaction Date
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Bank
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Cheque Number
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Amount Paid
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Narration
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Processed By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {transactions.map((transaction, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6 text-sm text-gray-900">
                          {transaction?.transaction_date}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700">
                          {transaction?.bank}
                        </td>
                        <td className="py-4 px-6 text-sm font-medium text-gray-900">
                          {transaction?.cheque_number}
                        </td>
                        <td className="py-4 px-6 text-sm font-semibold text-green-600">
                          GH₵{" "}
                          {parseFloat(transaction?.amount_paid || 0).toFixed(2)}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700">
                          {transaction?.narration || "N/A"}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700">
                          {transaction?.processed_by || "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-20 text-center">
                  <div className="text-gray-400 mb-2">
                    <Receipt className="mx-auto h-12 w-12" strokeWidth={1.5} />
                  </div>
                  <p className="text-gray-500 text-sm">
                    No transactions found for this supplier
                  </p>
                </div>
              )}
          </div>

          {/* Pagination */}
          {((activeTab === "invoices" && invoices?.length > 0) ||
            (activeTab === "transactions" && transactions?.length > 0)) && (
              <div className="p-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing page {currentPage} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg transition-all ${currentPage > 1
                        ? "text-gray-600 hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                        }`}
                    >
                      <ChevronLeft size={20} />
                    </button>

                    <div className="flex items-center gap-1">
                      {renderPageNumbers()}
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                        ? "text-gray-600 hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                        }`}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Payment Modal */}
      <SupplierTransaction
        modalOpen={paymentModalOpen}
        setModalOpen={setPaymentModalOpen}
        supplier={{
          ...supplier,
          total_outstanding: financeSummary?.outstanding_balance,
        }}
      />
    </div>
  );
};

export default SupplierDetail;
